import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fetchAvailability, createAppointment } from "@/lib/api";
import { Reveal } from "./motion";

const SERVICES = [
  "Individual Therapy",
  "Support for Parents of Neurodivergent Children",
  "Psychological Assessment",
  "Events & Workshops",
];

const toISO = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const Booking = () => {
  const [service, setService] = useState(SERVICES[0]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const iso = toISO(date);
  const { data: availability, isFetching } = useQuery({
    queryKey: ["availability", iso],
    queryFn: () => fetchAvailability(iso),
    enabled: !!iso,
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const disabledDay = (d) => d < today || d.getDay() === 0 || d.getDay() === 6;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please choose a date and time for your session.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await createAppointment({
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service,
        date: iso,
        time,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Your session request has been received.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(false);
    setDate(null);
    setTime(null);
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-prism-sand/50 border-y border-border overflow-hidden"
    >
      <div className="prism-aura absolute inset-0 opacity-50 -z-0" />
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.28em] mb-5" style={{ color: "hsl(var(--muted-foreground))" }}>
                <span className="h-1.5 w-6 rounded-full prism-line" /> Booking
              </p>
              <h2 className="font-serif font-light tracking-tight leading-tight text-primary text-4xl sm:text-5xl lg:text-6xl">
                Book a gentle first step
              </h2>
              <p className="mt-6 text-lg font-light leading-relaxed" style={{ color: "hsl(var(--muted-foreground))" }}>
                Choose a service, pick a time that suits you, and share a little
                about what brings you here. Dr. Fee will personally confirm your
                appointment by email.
              </p>
              <div className="mt-8 flex items-center gap-3 text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
                <CalendarDays size={18} /> Weekdays · 9:00 – 17:00
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] border border-border bg-card p-6 sm:p-10">
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div
                      key="done"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="py-16 text-center"
                      data-testid="booking-success"
                    >
                      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-prism-sage/60">
                        <Check className="text-primary" size={28} />
                      </div>
                      <h3 className="font-serif text-3xl font-light text-primary">
                        Thank you, {form.name.split(" ")[0] || "friend"}.
                      </h3>
                      <p className="mt-4 max-w-md mx-auto font-light" style={{ color: "hsl(var(--muted-foreground))" }}>
                        Your request for {service} on {iso} at {time} has been
                        received. You&apos;ll receive a confirmation at {form.email}.
                      </p>
                      <Button
                        data-testid="booking-reset"
                        onClick={reset}
                        variant="outline"
                        className="mt-8 rounded-full h-12 px-8"
                      >
                        Book another
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onSubmit={submit}
                      className="space-y-8"
                    >
                      {/* Service */}
                      <div>
                        <Label className="text-sm text-primary">Service</Label>
                        <div className="mt-3 flex flex-wrap gap-2.5">
                          {SERVICES.map((s) => (
                            <button
                              key={s}
                              type="button"
                              data-testid={`service-pill-${s}`}
                              onClick={() => setService(s)}
                              className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${
                                service === s
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "border-border text-text-secondary hover:border-primary/50"
                              }`}
                              style={service === s ? {} : { color: "hsl(var(--muted-foreground))" }}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Calendar */}
                        <div>
                          <Label className="text-sm text-primary">Select a date</Label>
                          <div className="mt-3 rounded-2xl border border-border bg-background p-2 inline-block" data-testid="booking-calendar">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={(d) => {
                                setDate(d);
                                setTime(null);
                              }}
                              disabled={disabledDay}
                            />
                          </div>
                        </div>

                        {/* Times */}
                        <div>
                          <Label className="text-sm text-primary">Available times</Label>
                          <div className="mt-3 min-h-[120px]">
                            {!date && (
                              <p className="text-sm font-light" style={{ color: "hsl(var(--muted-foreground))" }}>
                                Please select a weekday to see available times.
                              </p>
                            )}
                            {date && isFetching && (
                              <div className="flex items-center gap-2 text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
                                <Loader2 className="animate-spin" size={16} /> Checking availability…
                              </div>
                            )}
                            {date && !isFetching && availability?.closed && (
                              <p className="text-sm font-light" style={{ color: "hsl(var(--muted-foreground))" }}>
                                We&apos;re closed on this day. Please choose a weekday.
                              </p>
                            )}
                            {date && !isFetching && !availability?.closed && (
                              <div className="grid grid-cols-3 gap-2.5">
                                {availability?.slots?.map((sl) => (
                                  <button
                                    key={sl.time}
                                    type="button"
                                    data-testid={`time-slot-${sl.time}`}
                                    disabled={!sl.available}
                                    onClick={() => setTime(sl.time)}
                                    className={`rounded-xl border py-2.5 text-sm transition-colors duration-300 disabled:opacity-30 disabled:line-through ${
                                      time === sl.time
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "border-border text-primary hover:border-primary/50"
                                    }`}
                                  >
                                    {sl.time}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <Label htmlFor="name" className="text-sm text-primary">Full name</Label>
                          <Input
                            id="name"
                            data-testid="booking-name"
                            value={form.name}
                            onChange={set("name")}
                            placeholder="Your name"
                            className="mt-2 h-12 rounded-xl bg-background"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-sm text-primary">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            data-testid="booking-email"
                            value={form.email}
                            onChange={set("email")}
                            placeholder="you@email.com"
                            className="mt-2 h-12 rounded-xl bg-background"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="phone" className="text-sm text-primary">Phone (optional)</Label>
                          <Input
                            id="phone"
                            data-testid="booking-phone"
                            value={form.phone}
                            onChange={set("phone")}
                            placeholder="Optional"
                            className="mt-2 h-12 rounded-xl bg-background"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="message" className="text-sm text-primary">What brings you here? (optional)</Label>
                          <Textarea
                            id="message"
                            data-testid="booking-message"
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Share as much or as little as you like."
                            className="mt-2 rounded-xl bg-background min-h-[110px]"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        data-testid="booking-submit"
                        disabled={submitting}
                        className="w-full h-14 rounded-full text-base"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="animate-spin" size={18} /> Sending…
                          </span>
                        ) : (
                          "Request appointment"
                        )}
                      </Button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
